/* eslint-disable no-nested-ternary */
import React, { useEffect } from 'react';

import { IconCalendar, IconEmail, IconGlobe } from '@wds/icons';
import { Box } from '@wds/box';

import PropTypes from 'prop-types';
import TextCustomField from './TextCustomField';
import DropdownCustomField from './DropdownCustomField';
import DateCustomField from './DateCustomField';
import NumberCustomField from './NumberCustomField';

import { STRINGS, CustomFieldTypes } from './constants';

const CustomField = (props) => {
	const {
		id,
		type,
		label,
		value,
		onChange,
		required,
		readOnly,
		errors,
		setErrors,
		helperText,
		defaultValue,
		...rest
	} = props;
	const fieldProps = {
		id, label, value, onChange, required, readOnly, errors, setErrors, helperText, defaultValue,
	};

	const customFieldTypeMap = {
		0: {
			type: 'text',
			label: 'Text',
			addOn: null,
			// eslint-disable-next-line no-shadow,no-nested-ternary
			validator: (v) => (required && v.length < 1
				? STRINGS.requiredField
				: v.length > 255
					? STRINGS.textFieldMaxLengthError
					: ''),
		}, // short text
		1: {
			type: 'dropdown',
			label: 'Choice',
			icon: null,
			helperText: '',
			validator: (v, choices) => (choices.includes(v)
				? ''
				: STRINGS.requiredChoice),
		}, // dropdown
		2: {
			type: 'email',
			label: 'Email',
			addOn: <IconEmail />,
			validator: (v) => (required && v.length < 1
				? STRINGS.requiredField
				: v.length > 254
					? STRINGS.emailMaxLengthError
					: v && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v)
						? STRINGS.invalidEmail
						: ''),
		}, // email
		3: {
			type: 'number',
			label: 'Integer',
			addOn: null,
			validator: (v) => (required && v.length < 1
				? STRINGS.requiredField
				: v.length > 255
					? STRINGS.textFieldMaxLengthError
					: value && !/^\d+$/.test(v)
						? STRINGS.invalidNumber
						: ''),
		}, // number
		6: {
			type: 'url',
			label: 'URL',
			addOn: <IconGlobe />,
			validator: (v) => (required && v.length < 1
				? STRINGS.requiredField
				: v.length > 255
					? STRINGS.textFieldMaxLengthError
					: v && !/^(https?|ftp):\/\/[^\s/$.?#].[^\s]+(\.[a-zA-Z]{2,})+$/.test(v)
						? STRINGS.invalidURL
						: ''),
		}, // website
		7: {
			type: 'longtext',
			label: 'LongText',
			addOn: null,
			validator: (v) => (required && v.length < 1
				? STRINGS.requiredField
				: v.length > 512
					? STRINGS.longTextFieldMaxLengthError
					: ''),
		}, // long text
		8: { label: 'Date', addOn: <IconCalendar />, helperText: '', validator: null }, // date
	};

	switch (type) {
	case CustomFieldTypes.Text:
	case CustomFieldTypes.URL:
		return (
			<Box my={1}><TextCustomField {...fieldProps} settings={customFieldTypeMap[type]} /></Box>
		);
	case CustomFieldTypes.Choice:
		return (
			<Box my={1}>
				<DropdownCustomField
					{...fieldProps}
					name={label}
					choices={rest.choices}
					settings={customFieldTypeMap[type]}
				/>
			</Box>
		);
	case CustomFieldTypes.Email:
		return (
			<Box my={1}>
				<TextCustomField {...fieldProps} settings={customFieldTypeMap[type]} />
			</Box>
		);
	case CustomFieldTypes.Integer:
	case CustomFieldTypes.Decimal:
		return (
			<Box my={1}>
				<NumberCustomField {...fieldProps} settings={customFieldTypeMap[type]} />
			</Box>
		);
	case CustomFieldTypes.LongText:
		return (
			<Box my={1}>
				<TextCustomField {...fieldProps} settings={customFieldTypeMap[type]} />
			</Box>
		);
	case CustomFieldTypes.Date:
		return (
			<Box my={1}>
				<DateCustomField {...fieldProps} name={label} />
			</Box>
		);
	default:
		return null;
	}
};

CustomField.propTypes = {
	id: PropTypes.number.isRequired,
	type: PropTypes.number.isRequired,
	label: PropTypes.string.isRequired,
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
	required: PropTypes.bool,
	readOnly: PropTypes.bool,
	errors: PropTypes.oneOfType([
		PropTypes.object,
		PropTypes.instanceOf(undefined),
	]),
	setErrors: PropTypes.func.isRequired,
	helperText: PropTypes.string,
	defaultValue: PropTypes.any,
};
CustomField.defaultProps = {
	value: '',
	required: false,
	readOnly: false,
	helperText: '',
	errors: {},
	defaultValue: null,
};
export default CustomField;
